import React, { useState } from "react";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { SortIcon } from "../../../../../asssets/icons";
import { Button, Input } from "../../../../form";

const EstablishmentsListEditor = ({ establishments = [], onChange }) => {
  const [newItem, setNewItem] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const handleAdd = () => {
    if (!newItem.trim()) return;
    onChange([...establishments, newItem.trim()]);
    setNewItem("");
  };

  const handleRemove = (index) => {
    onChange(establishments.filter((_, i) => i !== index));
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditingValue(establishments[index]);
  };

  const handleEditSave = () => {
    if (editingIndex === null || !editingValue.trim()) return;
    const updated = [...establishments];
    updated[editingIndex] = editingValue.trim();
    onChange(updated);
    setEditingIndex(null);
    setEditingValue("");
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingValue("");
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(establishments);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    onChange(items);
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <h3 className="font-semibold mb-4">Establishments List for Profile Form</h3>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="establishments">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2 mb-4">
              {establishments.map((item, index) => (
                <Draggable
                  key={`establishment-${index}`}
                  draggableId={`establishment-${index}`}
                  index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className="flex items-center gap-2 bg-white p-3 rounded-md shadow-sm !top-auto !left-auto">
                      <span {...provided.dragHandleProps} className="w-4 h-4 cursor-grab">
                        <SortIcon />
                      </span>
                      <span className="text-xs bg-[#515151] text-white px-2 py-1 rounded">
                        {index + 1}
                      </span>

                      {editingIndex === index ? (
                        <div className="flex-1 flex items-center gap-2">
                          <input
                            type="text"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            className="flex-1 px-2 py-1 border rounded text-sm"
                            autoFocus
                          />
                          <button
                            onClick={handleEditSave}
                            className="px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600">
                            ✓
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="px-2 py-1 bg-gray-400 text-white rounded text-sm hover:bg-gray-500">
                            ✕
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="flex-1 text-sm">{item}</span>
                          <button
                            onClick={() => handleEdit(index)}
                            className="px-2 py-1 text-blue-500 hover:text-blue-700 text-sm">
                            Edit
                          </button>
                          <MinusIcon
                            className="w-5 h-5 stroke-2 stroke-white bg-red-500 rounded cursor-pointer hover:bg-red-600"
                            onClick={() => handleRemove(index)}
                          />
                        </>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {establishments.length === 0 && (
        <p className="text-gray-400 text-sm mb-4">No establishments added yet.</p>
      )}

      <div className="flex items-center gap-2 border-t pt-4">
        <Input
          placeholder="Enter new establishment..."
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          className="flex-1 !mb-0"
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <PlusIcon
          className="w-8 h-8 stroke-2 stroke-white bg-[#606060] rounded-md cursor-pointer hover:bg-[#505050]"
          onClick={handleAdd}
        />
      </div>
    </div>
  );
};

export default EstablishmentsListEditor;
